import { Component, EventEmitter, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CAMPAIGN_STAGES, PAGE_NAMES, ROLES, ROLES_TYPE, CAMPAIGN_STUDIO_STATUS } from '../../../core/helpers/constants';
import { createGuid, getRolesForPage } from '../../../core/helpers/utility';
import { IUser } from '../../../core/models/auth.models';
import { ICampaign, ICampaignAddAccount } from '../../../core/models/campaigns.model';
import { IClient } from '../../../core/models/clients.models';
import { ISalesRep } from '../../../core/models/datafeed.models';
import { DataFeedService } from '../../../shared/api/datafeed.service';
import { AppService } from '../../../store/app.service';
import { ClientsApiService } from '../../clients/clients.apiservice';
import { CampaignsApiService } from '../campaigns.apiservice';
import { InstanceService } from '../../../core/instance/instance.service';

@Component({
  selector: 'app-edit-campaign',
  templateUrl: './edit-campaign.component.html',
  styleUrls: ['./edit-campaign.component.scss']
})
export class EditCampaignComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Input() iCampaignId: string | null = null;
  @Input() iClientId: string | null = null;
  @Output() oSaveClicked = new EventEmitter<ICampaign>();
  public CAMPAIGN_STAGES = CAMPAIGN_STAGES;
  public CAMPAIGN_STUDIO_STATUS = CAMPAIGN_STUDIO_STATUS;
  public allPlatforms: Array<any> = [];
  public allBundles = ['Bundle1', 'Bundle2', 'Bundle3', 'Bundle4', 'Bundle5',];
  public defaultCRMNames = [
    { title: 'ClubReady', value: 'ClubReady' },
    { title: 'Mindbody', value: 'Mindbody' },
    { title: 'Loyalsnap', value: 'Loyalsnap' },
    { title: 'Glofox', value: 'Glofox' },
    { title: 'OTHER', value: 'Other' },
  ];
  isCustomCrmName = false;
  editCampaignForm!: FormGroup;
  submitted = false;

  allDms: Array<ISalesRep> = new Array<ISalesRep>();
  allClients: Array<IClient> = new Array<IClient>();
  allEmployees: Array<IUser> = new Array<IUser>();
  activeBundle: string = '';

  isAdmin: boolean = false;
  isExternal: boolean = false;
  canDelete: boolean = false;
  canRead: boolean = false;
  canWrite: boolean = false;
  isAddAcountMode: boolean = false;
  isUpdateAccountMode: boolean = false;
  currentAccountId: string = '';
  currentCampaignId: string = '';
  currentAdLabel: string = '';
  currentAdAccountId: string = '';
  isAddAccountSubmitted: boolean = false;
  options: any = null;
  campaignAdAccounts: Array<ICampaignAddAccount> = new Array<ICampaignAddAccount>();
  isEditMode: boolean = false;

  //new
  returnPath: string = '/pages/campaigns';

  get isModalMode(): boolean {
    return !!this.activeModal;
  }

  constructor(
    private dataFeedService: DataFeedService,
    private router: Router,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private campaignsService: CampaignsApiService,
    private clientService: ClientsApiService,
    private appService: AppService,
    public instance: InstanceService,
    @Optional() public activeModal?: NgbActiveModal) {

    this.editCampaignForm = this.formBuilder.group({
      id: [null],
      campaignId: ['', [Validators.required]],
      subscriptionId: [''],
      subscriptionActive: [false],
      name: ['', [Validators.required]],
      platform: ['', [Validators.required]],
      dmsId: ['', [Validators.required]],
      stageId: [null, [Validators.required]],
      clientId: ['', [Validators.required]],
      opportunityNotes: [''],
      crmId: [''],
      crmName: [''],
      employees: [null],
      street: ['', [Validators.maxLength(200)]],
      city: ['', [Validators.maxLength(200)]],
      state: ['', [Validators.maxLength(200)]],
      zipcode: ['', [Validators.maxLength(10)]],
      studioStatus: [null],
      locationPhone: ['', [Validators.maxLength(20)]],
      locationEmail: ['', [Validators.email, Validators.maxLength(100)]],
      bundle: [''],
      platformCampaignId: [''],
      platformAccountId: [''],
    });
  }

  ngOnInit(): void {
    this.allPlatforms = [
      ...(!this.instance.isAltLayout ? [{ value: 'Campaign Consulting', title: 'PLATFORMS.CAMPAIGN_CONSULTING'}] : []),
      ...(!this.instance.isAltLayout ? [{ value: 'Facebook', title: 'PLATFORMS.FACEBOOK'}] : [{ value: 'Facebook', title: 'PLATFORMS.META'}]),
      { value: 'Google', title: 'PLATFORMS.GOOGLE' },
      { value: 'TikTok', title: 'PLATFORMS.TIKTOK' },
      ...(!this.instance.isAltLayout ? [{ value: 'National Add On', title: 'PLATFORMS.NATIONAL_ADD_ON'}] : []),
      { value: 'Website', title: 'PLATFORMS.WEBSITE' },
      { value: 'YouTube', title: 'PLATFORMS.YOUTUBE' },
      { value: 'Other', title: 'PLATFORMS.OTHER' }
    ];

    this.f.crmName.valueChanges
      .pipe(takeUntil(this.destroyed$)
      ).subscribe(value => {
        if (value === "Other") {
          this.f.crmName.setValue('');
          this.isCustomCrmName = true;
        }
      })


    this.appService.getActiveUser$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(u => {
        this.isExternal = u.type == ROLES_TYPE.External;
        var r = getRolesForPage(u.roles, PAGE_NAMES.Campaigns);
        this.isAdmin = r.isAdmin;
        this.canRead = r.canRead;
        this.canWrite = r.canWrite;
        this.canDelete = r.canDelete;
        if (r.options)
          this.options = JSON.parse(r.options!)
      })
    let id = this.iCampaignId ?? this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!id;
    if (!this.isModalMode) {
      this.appService.setPageTitle$(id ? 'EDIT_CAMPAIGN' : 'ADD_CAMPAIGN');
    }
    if (id) {
      this.getCampaignDetails(id);
    }
    else {
      this.getNewCampaignId();
      if (this.iClientId) {
        this.f.clientId.setValue(this.iClientId);
        this.updateProspectClientDetails();
      }
    }
    if (id) {
      this.returnPath = '/pages/campaign-details/' + id;
    }
    this.getAllSalesReps();
    this.getAllClients();
  }

  getAllSalesReps() {
    this.dataFeedService.getAllUsersWithRole(ROLES.DMS.Code)
      .pipe(takeUntil(this.destroyed$)
      )
      .subscribe((response: any) => {
        if (response.good) {
          this.allDms = [...response.data];
          // this.addClientForm.patchValue({ brandId: response.data[0] });
        }
      });
  }

  getAllClients() {
    this.dataFeedService.getAllClients()
      .pipe(takeUntil(this.destroyed$)
      )
      .subscribe((response: any) => {
        if (response.good) {
          this.allClients = [...response.data];
          // this.addClientForm.patchValue({ brandId: response.data[0] });
        }
      });
  }

  getCampaignDetails(id: any) {
    this.campaignsService.getCampaignDetails(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        // let date: Date = new Date(response.data.nextActionDate);
        // let time = `${date.getHours()}${":"}${date.getMinutes()}`;
        this.editCampaignForm.patchValue(response.data);
        this.campaignAdAccounts = response.data.campaignAdAccounts;
        this.editCampaignForm.patchValue(
          {
            clientId: response.data.client.id,
            employees: response.data.employees.map((x: any) => ({ id: x.id, fullName: `${x.firstName} ${x.lastName}` }))
          });
        this.isCustomCrmName = !(this.defaultCRMNames.find(x => x.value === response.data.crmName));
      });
  }

  getNewCampaignId() {
    let guid = createGuid();
    this.f.id.setValue(guid);

    this.campaignsService.getNewCampaignId()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.data) this.f.campaignId.setValue(response.data);
      });
  }

  get f() { return this.editCampaignForm.controls; }

  updateProspectClientDetails() {
    let clientId = this.f.clientId.value;
    this.f.dmsId.setValue('');
    if (clientId) {
      this.clientService.getClientDetails(clientId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          if (response.data.dmsId)
            this.f.dmsId.setValue(response.data.dmsId);
        });
    }
  }
  onShowDropdown() {
    this.isCustomCrmName = false;
    this.editCampaignForm.patchValue({ crmName: this.defaultCRMNames[0].value })
  }

  onSubmit() {
    this.submitted = true;
    if (this.editCampaignForm.invalid)
      return;
    if (this.f.id.value === null)
      this.f.id.setValue(createGuid())

    let formValues: ICampaign = this.editCampaignForm.value;
    formValues.campaignAdAccounts = this.campaignAdAccounts;
    formValues = this.trimObjectStrings(formValues);

    this.campaignsService.saveCampaign(formValues)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.oSaveClicked.emit(formValues);
        if (this.isModalMode) {
          this.activeModal?.close(formValues);
          return;
        }
        this.router.navigate(['/pages/campaign-details/', formValues.id]);
      });
  }

  onCancel() {
    if (this.isModalMode) {
      this.activeModal?.dismiss('cancel');
      return;
    }

    this.router.navigate([this.returnPath]);
  }

  // This function will recursively iterate through the object
  trimObjectStrings(obj: any): any {
    // Iterate over each property of the object
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        
        if (typeof value === 'string') {
          // Trim the string if the value is a string
          obj[key] = value.trim();
        } else if (typeof value === 'object' && value !== null) {
          // Recursively call the function if the value is an object (or array)
          this.trimObjectStrings(value);
        }
      }
    }
    return obj;
  }

  deleteAdAccount(adAccount: any){
    const index = this.campaignAdAccounts.findIndex((Ad: { id: string; }) => Ad.id == adAccount.id);
    this.campaignAdAccounts.splice(index, 1);
  }

  OnAddAccontClick(){
    this.isAddAcountMode = true;
    this.isUpdateAccountMode = false;
    this.currentAccountId = '';
    this.currentCampaignId = '';
    this.currentAdLabel = '';
    this.currentAdAccountId = '';
    this.isAddAccountSubmitted = false;
  }

  onEditAccountClick(adAccountId: string){
    var adAccount = this.campaignAdAccounts.find((Ad => Ad.id == adAccountId))
    if(adAccount){
      this.isAddAcountMode = true;
      this.isUpdateAccountMode = true;
      this.currentAccountId = adAccount.platformAccountID;
      this.currentCampaignId = adAccount.platformCampaignID;
      this.currentAdLabel = adAccount.adLabel;
      this.currentAdAccountId = adAccount.id;
      this.isAddAccountSubmitted = false;
    }
  }

  CancelAddMode(){
    this.isAddAcountMode = false;
    this.isUpdateAccountMode = false;
  }

  updateAccountDetail(){
    var adAccount = this.campaignAdAccounts.find((Ad => Ad.id == this.currentAdAccountId))
    if(adAccount){
      adAccount.adLabel = this.currentAdLabel;
      adAccount.platformAccountID = this.currentAccountId;
      adAccount.platformCampaignID = this.currentCampaignId;
    }
    this.isAddAcountMode = false;
    this.isUpdateAccountMode = true;
  }

  addAccountDetail(){
    var adObj = {
      id:createGuid(),
      rmCampaignId:this.f.id.value,
      platformCampaignID: this.currentCampaignId,
      platformAccountID: this.currentAccountId,
      adLabel: this.currentAdLabel
    }

    this.campaignAdAccounts.push(adObj);
    this.isAddAcountMode = false;
    this.isUpdateAccountMode = true;
  }

  ngAfterViewInit() {
  }
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
